import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Hammer, Gem, ArrowDown } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";
import { IMAGES, SERVICES, PHONE, PHONE_HREF, LICENSE } from "@/data/site";

const SPOTLIGHTS = [
  {
    image: IMAGES.blueDoor,
    title: "Exteriors That Command the Street",
    text: "Stacked stone, standing-seam metal roofing and an entry that announces itself - curb appeal engineered, not improvised.",
  },
  {
    image: IMAGES.bathMarble,
    title: "Bathrooms Built Like Spas",
    text: "Multi-head shower systems, sculptural freestanding tubs and marble-look tilework detailed to the millimetre.",
  },
  {
    image: IMAGES.kitchenGeometric,
    title: "Kitchens Made for Gathering",
    text: "Waterfall islands, custom cabinetry and statement backsplashes - the room where your home's story gets written.",
  },
];

const VALUES = [
  {
    icon: Hammer,
    num: "01",
    title: "One Project at a Time",
    text: "We focus on each individual project - personalized solutions, superior craftsmanship, and clients who come back and refer.",
  },
  {
    icon: ShieldCheck,
    num: "02",
    title: "Licensed & Certified",
    text: `A State of Florida Certified General Contractor - ${LICENSE} - held to the highest industry standards on every build.`,
  },
  {
    icon: Gem,
    num: "03",
    title: "Detail Obsessed",
    text: "From foundation to rooftop, every decision is a brushstroke. We sweat the corners nobody sees.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <div data-testid="home-page">
      <Seo
        title="McMullen Construction Co. - Remodel & New Construction in Florida"
        description="McMullen Construction Co. - licensed Florida general contractor (CGC #1533095). Remodels, custom homes, roofing, design & engineering. Call 321-313-0996."
      />
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
          <img src={IMAGES.blueDoor} alt="" className="h-[120%] w-full object-cover img-spotlight" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/80 via-[#08080A]/55 to-[#08080A]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#08080A]/75 via-transparent to-[#08080A]/30" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pt-24 pb-28">
          <MaskedLine delay={0.2}>
            <span className="eyebrow">Licensed Florida General Contractor - {LICENSE}</span>
          </MaskedLine>
          <h1 className="mt-7 font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#F4F2EE]">
            <MaskedLine delay={0.35}>Your Trusted</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="italic text-gold-bright">Local Contractors.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.7}>
            <span className="mt-7 block max-w-xl text-base md:text-lg leading-relaxed text-[#C9C7C2]">
              Residential &amp; Commercial Remodel &amp; new construction, crafted one project at a time. From the foundation to the rooftop, every detail is a brushstroke of your imagination.
            </span>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-start-project"
              className="group bg-[#D4AF37] px-7 py-3.5 text-xs uppercase tracking-[0.22em] font-semibold text-black hover:bg-[#F3E081] transition-colors duration-300 flex items-center gap-2"
            >
              Start Your Project
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
            <Link
              to="/gallery"
              data-testid="hero-cta-view-work"
              className="border border-[rgba(212,175,55,0.5)] px-7 py-3.5 text-xs uppercase tracking-[0.22em] text-[#F3E081] hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-300"
            >
              View Our Work
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-16 flex flex-wrap gap-x-12 gap-y-4 font-mono-accent text-xs tracking-[0.2em] text-[#D4AF37]"
            data-testid="hero-trust-bar"
          >
            <a href={PHONE_HREF} className="hover:text-[#F3E081] transition-colors" data-testid="hero-phone-link">{PHONE}</a>
            <span>{LICENSE}</span>
            <span>Remodel &amp; New Construction</span>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#8A7322]"
          aria-hidden="true"
        >
          <ArrowDown className="h-5 w-5 animate-bounce" />
        </motion.div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28" data-testid="services-bento-section">
        <Reveal>
          <p className="eyebrow">What We Build</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#F3E081]">Seven disciplines. One standard.</h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal
              key={s.id}
              delay={i * 0.08}
              className={i === 0 || i === 3 ? "md:col-span-7" : "md:col-span-5"}
            >
              <Link
                to="/services"
                data-testid={`bento-service-${s.id}`}
                className="group relative block h-72 sm:h-80 overflow-hidden border border-[rgba(212,175,55,0.2)] hover:border-[rgba(243,224,129,0.55)] transition-colors duration-500"
              >
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover img-spotlight transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080A] via-[#08080A]/35 to-transparent" aria-hidden="true" />
                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">0{i + 1}</p>
                  <h3 className="mt-2 font-display text-xl sm:text-2xl font-medium text-[#F4F2EE]">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#A1A1AA] max-w-md">{s.blurb}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ink-2 border-y border-[rgba(212,175,55,0.15)] py-20 sm:py-28" data-testid="spotlight-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">Signature Work</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#F3E081]">Craft you can walk through.</h2>
          </Reveal>

          <div className="mt-16 space-y-20">
            {SPOTLIGHTS.map((sp, i) => (
              <div key={sp.title} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${i % 2 === 1 ? "" : ""}`}>
                <Reveal className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="gold-frame-offset" data-testid={`spotlight-image-${i}`}>
                    <img src={sp.image} alt={sp.title} loading="lazy" className="relative z-10 w-full h-80 sm:h-[26rem] object-cover img-spotlight" />
                  </div>
                </Reveal>
                <Reveal delay={0.15} className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <p className="font-mono-accent text-xs tracking-[0.3em] text-[#8A7322]">FEATURED - 0{i + 1}</p>
                  <h3 className="mt-4 font-display text-2xl sm:text-3xl text-[#F4F2EE]">{sp.title}</h3>
                  <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#A1A1AA]">{sp.text}</p>
                  <Link
                    to="/gallery"
                    data-testid={`spotlight-link-${i}`}
                    className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#D4AF37] hover:text-[#F3E081] transition-colors"
                  >
                    Explore the gallery <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28" data-testid="values-section">
        <Reveal>
          <p className="eyebrow">The McMullen Standard</p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {VALUES.map((v, i) => (
            <Reveal key={v.num} delay={i * 0.1}>
              <div
                className="h-full bg-[#121216]/90 border border-[rgba(212,175,55,0.2)] p-8 hover:border-[rgba(243,224,129,0.5)] transition-colors duration-500"
                data-testid={`value-card-${i}`}
              >
                <v.icon className="h-6 w-6 text-[#D4AF37]" aria-hidden="true" />
                <p className="mt-6 font-mono-accent text-xs tracking-[0.3em] text-[#8A7322]">{v.num}</p>
                <h3 className="mt-3 font-display text-xl sm:text-2xl text-[#F4F2EE]">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#A1A1AA]">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-[rgba(212,175,55,0.15)] py-24 sm:py-32" data-testid="cta-section">
        <img src={IMAGES.kitchenFarmhouse} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-15 img-spotlight" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080A] via-transparent to-[#08080A]" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#F4F2EE] text-balance">
              Ready to build your <span className="italic text-gold-bright">dream?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-base md:text-lg text-[#A1A1AA]">
              Tell us about your project - we respond personally, one build at a time.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="cta-contact-button"
                className="bg-[#D4AF37] px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold text-black hover:bg-[#F3E081] transition-colors duration-300"
              >
                Request a Consultation
              </Link>
              <a
                href={PHONE_HREF}
                data-testid="cta-phone-button"
                className="border border-[rgba(212,175,55,0.5)] px-8 py-4 text-xs uppercase tracking-[0.22em] text-[#F3E081] hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-300"
              >
                Call {PHONE}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
